import { useFrame, useThree } from '@react-three/fiber'
import { useRef, useState } from 'react'
import { BufferGeometry, Material, Mesh, RGBFormat, Texture } from 'three'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader'

const DEFAULT_POLLING_INTERVAL_SEC = 1

interface TextureImage {
  width: number
  height: number
  data?: {
    byteLength: number
  }
}

interface UseEstimateVRAM {
  scene: GLTF['scene'] | undefined
  pollingInterval?: number
}

const getGeometryBytes = (geom: BufferGeometry): number => {
  let bytes = 0
  if (geom.attributes) {
    Object.values(geom.attributes).forEach((attr) => {
      bytes += attr.count * attr.itemSize * 4
    })
  }
  if (geom.index) {
    bytes += geom.index.count * (geom.index.array.BYTES_PER_ELEMENT || 4)
  }
  return bytes
}

interface CompressedMipmap {
  data?: { byteLength: number }
}

const getTextureBytes = (tex: Texture): number => {
  // Compressed textures (e.g. KTX2/Basis via KHR_texture_basisu) carry the
  // exact GPU payload in their mipmap chain. `tex.image` is only
  // { width, height }, so the uncompressed estimate below would wildly
  // overstate them (RGBA8 vs 4-8 bits per pixel).
  if ((tex as { isCompressedTexture?: boolean }).isCompressedTexture) {
    const mipmaps = (tex.mipmaps ?? []) as CompressedMipmap[]
    return mipmaps.reduce((sum, mip) => sum + (mip.data?.byteLength ?? 0), 0)
  }

  const textureImage = tex.image as TextureImage
  if (!tex.image || !textureImage.width || !textureImage.height) {
    return 0
  }

  if (textureImage.data?.byteLength) {
    return textureImage.data.byteLength
  }

  const { width, height } = textureImage
  let channels = 4

  if (tex.format === RGBFormat) {
    channels = 3
  }

  let texBytes = width * height * channels
  if (tex.generateMipmaps) {
    texBytes *= 1.33
  }
  return texBytes
}

const getMaterialBytes = (
  mat: Material,
  countedTextures: Set<Texture>
): number => {
  let bytes = 0
  Object.values(mat).forEach((value) => {
    const tex = value as Texture
    if (tex && tex.isTexture && tex.image && !countedTextures.has(tex)) {
      countedTextures.add(tex)
      bytes += getTextureBytes(tex)
    }
  })
  return bytes
}

const useCalculateVRAM = ({
  scene,
  pollingInterval = DEFAULT_POLLING_INTERVAL_SEC,
}: UseEstimateVRAM) => {
  const { clock } = useThree()
  const [VRAMInBytes, setVRAMInBytes] = useState(0)
  const lastUpdate = useRef(0)

  useFrame(() => {
    const elapsed = clock.getElapsedTime()
    if (elapsed - lastUpdate.current < pollingInterval) {
      return
    }
    lastUpdate.current = elapsed

    const countedTextures = new Set<Texture>()
    let totalBytes = 0

    scene?.traverse((obj) => {
      const mesh = obj as Mesh
      if (!mesh.isMesh) {
        return
      }

      const geom = mesh.geometry
      if (geom) {
        totalBytes += getGeometryBytes(geom)
      }

      const materials = Array.isArray(mesh.material)
        ? mesh.material
        : [mesh.material]
      materials.forEach((mat) => {
        totalBytes += getMaterialBytes(mat, countedTextures)
      })
    })

    setVRAMInBytes(totalBytes)
  })

  const VRAMInMegaBytes = VRAMInBytes / 1024 / 1024
  const VRAM = Math.round(VRAMInMegaBytes)

  return { VRAM }
}

export default useCalculateVRAM
